import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../api';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Button } from '../../components/common/Button';
import LoadingScreen from '../../components/common/LoadingScreen';
import { FaChartLine, FaArrowLeft, FaDownload } from 'react-icons/fa';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// --- Styled Components ---
const TrendPageContainer = styled.div`padding: 2rem 3rem; background-color: ${({ theme }) => theme.colors.background}; min-height: 100vh;`;
const Header = styled.header`display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem;`;
const PageTitle = styled.h2`font-size: 2.2rem; color: ${({ theme }) => theme.colors.secondary}; display: flex; align-items: center; gap: 1rem; text-transform: capitalize;`;
const Card = styled.div`background-color: ${({ theme }) => theme.colors.white}; padding: 2rem; border-radius: 16px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);`;
const FilterContainer = styled.div`display: flex; gap: 1rem; align-items: center; margin-bottom: 2rem; padding: 1rem; background-color: #f9f9f9; border-radius: 8px;`;
const DateInput = styled.input`padding: 0.5rem; border-radius: 6px; border: 1px solid #ccc; font-family: inherit;`;

const metricDisplayNames = {
    followers_count: 'Seguidores',
    media_count: 'Número de Mídias',
    likes: 'Curtidas',
    comments: 'Comentários',
    views: 'Visualizações / Plays',
    reach: 'Alcance',
    shares: 'Compartilhamentos',
    saves: 'Salvos'
};

function TrendPage() {
    const { context, id, metricName } = useParams();
    const navigate = useNavigate();
    
    const [fullData, setFullData] = useState({ labels: [], values: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError('');
            const endpoint = `/api/history/${context}/${id}`;
            
            try {
                const response = await apiClient.get(endpoint);
                const data = response.data;

                setFullData({
                    labels: data.labels || [],
                    values: data[metricName] || []
                });
                
                if (data.labels && data.labels.length > 0) {
                    const firstDateStr = data.labels[0].split(' ')[0].split('/').reverse().join('-');
                    const lastDateStr = data.labels[data.labels.length - 1].split(' ')[0].split('/').reverse().join('-');
                    setStartDate(new Date(firstDateStr).toISOString().split('T')[0]);
                    setEndDate(new Date(lastDateStr).toISOString().split('T')[0]);
                }
            } catch (err) {
                setError(`Falha ao carregar o histórico de '${metricDisplayNames[metricName]}'.`);
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [context, id, metricName]);

    const filteredData = useMemo(() => {
        if (!fullData.labels || fullData.labels.length === 0) return { labels: [], datasets: [] };
        
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        
        const filteredLabels = [];
        const filteredValues = [];

        fullData.labels.forEach((label, index) => {
            const currentDate = new Date(label.split(' ')[0].split('/').reverse().join('-'));
            if ((!start || currentDate >= start) && (!end || currentDate <= end)) {
                filteredLabels.push(label);
                filteredValues.push(fullData.values[index]);
            }
        });

        const displayName = metricDisplayNames[metricName] || metricName;
        return {
            labels: filteredLabels,
            datasets: [{
                label: `Evolução de ${displayName}`,
                data: filteredValues,
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                tension: 0.1
            }]
        };
    }, [fullData, startDate, endDate, metricName]);

    // --- NOVA FUNÇÃO DE EXPORTAÇÃO ---
    const handleExportCSV = () => {
        const labels = filteredData.labels;
        const values = filteredData.datasets[0]?.data;
        const displayName = metricDisplayNames[metricName] || metricName;

        if (!labels || labels.length === 0) return;

        let csvContent = "Data;Valor\n";
        labels.forEach((label, index) => {
            csvContent += `${label};${values[index]}\n`;
        });

        const bom = "\uFEFF";
        const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Historico_${displayName}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <LoadingScreen message="Carregando Gráfico..." />;
    if (error) return <div>{error}</div>;

    return (
        <TrendPageContainer>
            <Header>
                <PageTitle><FaChartLine /> {metricDisplayNames[metricName] || 'Tendência'}</PageTitle>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    {/* Botão de Exportar */}
                    <Button variant="secondary" onClick={handleExportCSV}>
                        <FaDownload /> Exportar CSV
                    </Button>
                    <Button onClick={() => navigate('/home')}><FaArrowLeft /> Voltar</Button>
                </div>
            </Header>
            <Card>
                <FilterContainer>
                    <label htmlFor="start-date">De:</label>
                    <DateInput id="start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    <label htmlFor="end-date">Até:</label>
                    <DateInput id="end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </FilterContainer>
                <Line data={filteredData} options={{ responsive: true }} />
            </Card>
        </TrendPageContainer>
    );
}

export default TrendPage;